import { Box, CheckBox, Text } from 'grommet';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { EditableText, SelectableText } from '../..';

import { Commercial, CommercialType, TUpdateCommercialRequest } from '@talon/web/lib/api';
import { useCommercialStore, useEstimateStore } from '@talon/web/src/context';
import { USD } from '@talon/web/src/utils/currency';

export interface ICommercialRowProps {
  /** From the panel's list, which already has every field the row shows */
  commercial: Commercial;
  onChecked: () => void;
  checked: boolean;
}

export const CommercialRow = observer(({ commercial: listedCommercial, onChecked, checked }: ICommercialRowProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();
  const commercialStore = useCommercialStore();

  /** State **/
  // Rendered straight from the panel's list. Each row used to refetch its commercial with nine
  // relations it never displays, and showed a blank row until that returned
  const [commercial, setCommercial] = useState<Commercial>(listedCommercial);
  const [wages, setWages] = useState(0);
  const [total, setTotal] = useState(0);

  /** Computed **/
  const usageEstimate = estimateStore.getTempUsageEstimate(commercial?.id);

  /** Methods **/

  // Reads estimateStore.tempEstimate directly (rather than a value captured during
  // render) so this isn't tracked as a render-time MobX dependency - otherwise every
  // row re-renders whenever *any* commercial's usage estimate is recalculated.
  const recalculateUsageEstimate = async () => {
    if (!commercial) return;

    const usageEstimate = estimateStore.tempEstimate?.usage_estimates.find(
      (usageEstimate) => usageEstimate.commercial?.id === commercial.id
    );

    if (usageEstimate) await estimateStore.updateUsageEstimate(usageEstimate.id, undefined, true);
  };

  /**
   * Update Commercial
   */
  const updateCommercial = async (update: TUpdateCommercialRequest) => {
    if (!commercial) return;
    const updatedCommercial = await commercialStore.updateCommercial(commercial.id, update);

    if (updatedCommercial) {
      setCommercial(updatedCommercial);

      // Only the commercial's type affects usage fee calculations (see the
      // usage-reset warning in CommercialEditRow) - title/duration edits don't,
      // so skip the costly whole-project estimate refresh for those.
      if (update.type !== undefined) await recalculateUsageEstimate();
    }
  };

  /** Effects **/

  useEffect(() => {
    if (usageEstimate) {
      let wages = 0;
      let total = 0;

      for (const item of usageEstimate.items) {
        wages += item.subtotal;
        total += item.subtotal_plus_fringe;
      }

      setWages(wages);
      setTotal(total);
    }
  }, [usageEstimate]);

  useEffect(() => {
    setCommercial(listedCommercial);
  }, [listedCommercial]);

  /** Render **/
  return (
    <Box justify="center" pad={{ vertical: 'xxsmall', horizontal: 'small' }}>
      <Box direction="row" align="center">
        <Box direction="row" gap="small" pad="small" align="center">
          <CheckBox onChange={onChecked} checked={checked} />
        </Box>

        <Box direction="row" gap="small" pad="small" align="center" width="20%">
          <EditableText val={commercial?.title ?? ''} onChange={(title) => updateCommercial({ title })} />
        </Box>

        <Box direction="row" gap="small" pad="small" align="center" width="30%">
          <Text>Type:</Text>
          <SelectableText
            options={Object.values(CommercialType).map((t) => ({ label: t, value: t }))}
            val={commercial?.type}
            onChange={(val) => updateCommercial({ type: val as CommercialType })}
          />
        </Box>

        <Box flex />

        <Box direction="row" justify="end">
          <Box direction="row" gap="xsmall" justify="end" align="center" width="">
            <Text size="small" weight={700}>
              WAGES:
            </Text>
            {<Text weight="bold">{USD(wages)}</Text>}
          </Box>
          <Box direction="row" gap="xsmall" justify="end" align="center" width="10rem">
            <Text size="small" weight={700}>
              TOTAL:
            </Text>
            {<Text weight="bold">{USD(total)}</Text>}
          </Box>
        </Box>

        <Box width="48px" height="48px" />
      </Box>
    </Box>
  );
});
